import {Tag} from "antd";
import React, {CSSProperties} from "react";

const TAG_COLOR_PALETTE = [
    "#2563eb",
    "#dc2626",
    "#7c3aed",
    "#16a34a",
    "#c026d3",
    "#ea580c",
    "#0891b2",
    "#4f46e5",
    "#be123c",
    "#0f766e",
    "#ca8a04",
];
const TAG_BACKGROUND_COLOR = "#ffffff";
const TAG_BORDER_MIX_RATIO = 0.68;
const TAG_ACCENT_BORDER_WIDTH = 2;

const HASH_OFFSET_BASIS = 2166136261;
const HASH_PRIME = 16777619;
const HASH_MIX_1 = 2246822507;
const HASH_MIX_2 = 3266489909;

type Props = {
    value: string;
    className?: string;
    style?: CSSProperties;
};

function HashColorTag(props: Props) {
    const style = styleForValue(props.value);

    return (
        <Tag
            className={props.className}
            style={{
                ...style,
                ...props.style
            }}
            title={props.value}
        >
            {props.value}
        </Tag>
    );
}

function styleForValue(value: string): CSSProperties {
    const color = colorForValue(value);

    return {
        color,
        backgroundColor: TAG_BACKGROUND_COLOR,
        borderColor: mixColor(color, TAG_BACKGROUND_COLOR, TAG_BORDER_MIX_RATIO),
        borderBottomColor: color,
        borderBottomWidth: TAG_ACCENT_BORDER_WIDTH,
    };
}

function colorForValue(value: string) {
    const hash = hashString(normalizeValue(value));

    return TAG_COLOR_PALETTE[hash % TAG_COLOR_PALETTE.length];
}

function mixColor(color: string, mixWith: string, mixWithRatio: number) {
    const colorRgb = hexToRgb(color);
    const mixRgb = hexToRgb(mixWith);
    const colorRatio = 1 - mixWithRatio;

    const mixedRgb = colorRgb.map((value, index) =>
        Math.round(value * colorRatio + mixRgb[index] * mixWithRatio)
    );

    return `rgb(${mixedRgb[0]}, ${mixedRgb[1]}, ${mixedRgb[2]})`;
}

function hexToRgb(color: string) {
    return [
        parseInt(color.slice(1, 3), 16),
        parseInt(color.slice(3, 5), 16),
        parseInt(color.slice(5, 7), 16),
    ];
}

function normalizeValue(value: string) {
    return value
        .trim()
        .toLocaleLowerCase("cs-CZ")
        .normalize("NFD")
        .replace(/\p{Diacritic}/gu, "");
}

function hashString(value: string) {
    let hash = HASH_OFFSET_BASIS;

    for (let i = 0; i < value.length; i++) {
        hash ^= value.charCodeAt(i);
        hash = Math.imul(hash, HASH_PRIME);
    }

    hash ^= hash >>> 16;
    hash = Math.imul(hash, HASH_MIX_1);
    hash ^= hash >>> 13;
    hash = Math.imul(hash, HASH_MIX_2);
    hash ^= hash >>> 16;

    return hash >>> 0;
}

export default HashColorTag;
